import { useQuery } from "@tanstack/react-query";

import { api } from "../../lib/api";
import { presentError } from "../../lib/errors";
import { formatDateTime } from "../../lib/format";

export function MailHealthPage() {
	const health = useQuery({ queryKey: ["platform-mail-health"], queryFn: () => api.adminMailHealth() });
	if (health.isPending) return <p className="empty">Checking the mail server.</p>;
	if (health.isError || !health.data) return <p className="err">{presentError(health.error).title}</p>;
	return (
		<div>
			<p className="sub">{health.data.reachable ? "The mail server is reachable." : "The mail server did not respond."}</p>
			<div className="table-wrap">
				<table>
					<thead>
						<tr><th>Check</th><th>State</th><th>Detail</th></tr>
					</thead>
					<tbody>
						{health.data.checks.map((check) => (
							<tr key={check.id}>
								<td>{check.label}</td>
								<td>{check.state}</td>
								<td className="quiet">{check.detail}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}

export function QueuesPage() {
	const jobs = useQuery({ queryKey: ["platform-jobs"], queryFn: () => api.adminListJobs() });
	if (jobs.isPending) return <p className="empty">Loading queues.</p>;
	if (jobs.isError) return <p className="err">{presentError(jobs.error).title}</p>;
	const rows = jobs.data ?? [];
	if (rows.length === 0) {
		return (
			<div className="card">
				<h2>Queue is clear</h2>
				<p className="sub">Background jobs for domains, mailboxes and migrations show up here while they run.</p>
			</div>
		);
	}
	return (
		<div>
			<p className="sub">{rows.length} jobs. These are domain, mailbox and migration operations.</p>
			<div className="table-wrap">
			<table>
				<thead>
					<tr><th>Operation</th><th>Status</th><th>Resource</th></tr>
				</thead>
				<tbody>
					{rows.map((job) => (
						<tr key={job.id}>
							<td>{job.operation}</td>
							<td>{job.status}</td>
							<td className="quiet">{job.resourceType}</td>
						</tr>
					))}
				</tbody>
			</table>
			</div>
		</div>
	);
}

export function AbusePage() {
	const abuse = useQuery({ queryKey: ["platform-abuse"], queryFn: () => api.adminListAbuseEvents() });
	if (abuse.isPending) return <p className="empty">Loading abuse events.</p>;
	if (abuse.isError) return <p className="err">{presentError(abuse.error).title}</p>;
	const rows = abuse.data ?? [];
	if (rows.length === 0) {
		return (
			<div className="card">
				<h2>No abuse events</h2>
				<p className="sub">Nothing has been flagged. New reports and automatic detections will be listed here.</p>
			</div>
		);
	}
	return (
		<div className="table-wrap">
			<table>
				<thead>
					<tr><th>Workspace</th><th>Type</th><th>Status</th></tr>
				</thead>
				<tbody>
					{rows.map((event) => (
						<tr key={event.id}>
							<td>{event.organisationName}</td>
							<td>{event.eventType}</td>
							<td>{event.status}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

export function ServerSettingsPage() {
	const config = useQuery({ queryKey: ["platform-config"], queryFn: () => api.adminGetConfiguration() });
	if (config.isPending) return <p className="empty">Loading server settings.</p>;
	if (config.isError || !config.data) return <p className="err">{presentError(config.error).title}</p>;
	return (
		<div>
			<p className="sub">Policy for the mail server. Client hostnames stay on mail.key.my.</p>
			<div className="table-wrap">
				<table>
					<thead>
						<tr><th>Setting</th><th>Value</th></tr>
					</thead>
					<tbody>
						{config.data.settings.map((setting) => (
							<tr key={setting.key}>
								<td>{setting.key}</td>
								<td className="quiet">{setting.value}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}

export function AuditPage() {
	const audit = useQuery({ queryKey: ["platform-audit"], queryFn: () => api.adminAuditLog() });
	if (audit.isPending) return <p className="empty">Loading the audit log.</p>;
	if (audit.isError) return <p className="err">{presentError(audit.error).title}</p>;
	const rows = audit.data?.data ?? [];
	if (rows.length === 0) return <p className="empty">No audit events.</p>;
	return (
		<div className="table-wrap">
			<table>
				<thead>
					<tr><th>When</th><th>Who</th><th>Action</th></tr>
				</thead>
				<tbody>
					{rows.map((entry) => (
						<tr key={entry.id}>
							<td className="quiet">{formatDateTime(entry.createdAt)}</td>
							<td>{entry.actorLabel}</td>
							<td>{entry.action}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

const PUBLIC_ROUTES = [
	["/", "Sign-in"],
	["/manage, /hub, /mail, /site, /team, /social, /engage", "Workspace shell"],
	["/webmail", "Webmail"],
	["/api", "Control-plane API"],
	["/jmap", "Mailbox session"],
	["/healthz", "Health check"],
];

export function CaddyNote({ topic }: { topic: string }) {
	return (
		<div>
			<p className="sub">
				{topic} is served by Caddy on this server. This screen shows the public paths. It does not change them.
			</p>
			<div className="table-wrap">
				<table>
					<thead>
						<tr>
							<th>Path</th>
							<th>What it serves</th>
						</tr>
					</thead>
					<tbody>
						{PUBLIC_ROUTES.map(([path, serves]) => (
							<tr key={path}>
								<td>{path}</td>
								<td className="quiet">{serves}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}
